import { ChangeEvent, useEffect, useRef, useState } from "react"
import { MyDispatch, MySelector } from "../../redux/store"
import { toast } from "sonner"
import { useDispatch } from "react-redux"
import { joinEventAction, sendOtp, verifyOtp } from "../../redux/eventSlice"
import CreateProfile from "./CreateProfile"

let currentOTPIndex: number = 0

const VerifyOtp = () => {
  const dispatch = useDispatch<MyDispatch>()
  const { number, loading, eventId } = MySelector((state) => state.event)
  const inputRef = useRef<HTMLInputElement>(null)
  const [otp, setOtp] = useState<string[]>(new Array(6).fill(""))
  const [activeOtpIndex, setActiveOtpIndex] = useState<number>(0)
  const [resendAllow, setResendAllow] = useState(false)
  const [allowResendAfter, setAllowResendAfter] = useState(30)
  const [showCreateProfile, setShowCreateProfile] = useState(false)

  const handleChange = ({ target }: ChangeEvent<HTMLInputElement>) => {
    const { value } = target
    if (!/^\d*$/.test(value)) return

    const newOtp = [...otp]
    newOtp[currentOTPIndex] = value.substring(value.length - 1)

    if (!value) setActiveOtpIndex(currentOTPIndex - 1)
    else setActiveOtpIndex(currentOTPIndex + 1)

    setOtp(newOtp)
  }

  const verify = async () => {
    if (otp.join("").length !== 6) {
      toast.info("Please provide a 6 digit OTP.")
      return
    }

    try {
      const result = await dispatch(verifyOtp({ number, otp: otp.join("") })).unwrap()

      if (result.status === "OK") {
        // If user ID is available, join the event
        if (result.body?.id && eventId) {
          dispatch(joinEventAction({ eventId, userId: result.body.id }))
        } else {
          // User doesn't exist, show create profile form
          setShowCreateProfile(true)
        }
      }
    } catch (error) {
      // Error handling is already done in the redux slice
    }
  }

  const handleProfileCreated = (userId: string) => {
    setShowCreateProfile(false)
    // The CreateProfile component already handles joining the event
  }

  const resend = () => {
    dispatch(sendOtp(number))
    setResendAllow(false)
    setAllowResendAfter(30)
  }

  // to count down the timer
  useEffect(() => {
    if (allowResendAfter > 0) {
      const intervalId = setInterval(() => {
        setAllowResendAfter((prev) => (prev > 0 ? prev - 1 : 0))
      }, 1000)

      return () => clearInterval(intervalId)
    }
  }, [allowResendAfter])

  // to allow resend
  useEffect(() => {
    if (allowResendAfter === 0) {
      setResendAllow(true)
    }
  }, [allowResendAfter])

  // to focus on the current input
  useEffect(() => {
    inputRef.current?.focus()
  }, [activeOtpIndex])

  // Show create profile form if user doesn't exist
  if (showCreateProfile) {
    return <CreateProfile onProfileCreated={handleProfileCreated} />
  }

  return (
    <div>
      <p className="text-center font-[600] text-lg md:text-xl xl:text-2xl mb-[5px] md:mb-[8px] lg:mb-[10px] text-brightBlack dark:text-offWhite">
        Enter OTP
      </p>
      <p className="text-offGrey dark:text-grey text-sm md:text-sm xl:text-lg text-center font-[400]">
        Please check your Whatsapp for the OTP.
      </p>

      <div className="flex justify-evenly gap-2 md:gap-3 lg:gap-2 mb-4 mt-6 lg:mb-[24px] lg:mt-9 xl:mt-10">
        {otp?.map((_, i) => (
          <input
            key={i}
            ref={i === activeOtpIndex ? inputRef : null}
            className="w-[36px] h-[36px] md:w-[38px] md:h-[38px] lg:w-[50px] lg:h-[50px] border-[0.5px] border-offGrey/80 rounded-md lg:rounded-xl text-center outline-none text-[18.68px] bg-transparent focus-within:border focus:border-offWhite text-brightBlack dark:text-offWhite font-hanken text-sm lg:text-base"
            type="number"
            value={otp[i]}
            onChange={handleChange}
            onKeyDown={(e) => {
              currentOTPIndex = i
              if (e.key === "Backspace") setActiveOtpIndex(currentOTPIndex - 1)
              if (e.key === "Enter" && otp.join("").length === 6) verify()
            }}
          />
        ))}
      </div>

      <button
        onClick={verify}
        className={`w-[253px] md:w-[295px] lg:w-full rounded-2xl py-[12px] lg:py-[15.5px] font-[500] leading-[19.36px] outline-none border flex justify-center items-center text-sm xl:text-base ${
          otp?.join("").length === 6
            ? "bg-blackish dark:bg-golden text-white dark:text-blackish border-golden"
            : "bg-grey dark:bg-darkGrey text-white dark:text-offGrey border-grey dark:border-darkGrey"
        }`}
        disabled={otp?.join("").length < 6}
      >
        {loading ? (
          <span className="h-5 w-5 rounded-full border-2 border-current border-t-transparent animate-spin" />
        ) : (
          "Confirm"
        )}
      </button>

      {resendAllow ? (
        <button
          onClick={resend}
          className={`w-full rounded-xl mt-2 lg:mt-3 pt-[5px] font-[500] text-xs lg:text-sm outline-none flex justify-center items-center text-blackish dark:text-golden`}
        >
          Resend OTP
        </button>
      ) : (
        <p className="text-offGrey font-[400] text-xs lg:text-sm text-center mt-2 lg:mt-4 pt-[5px]">
          Didn't receive the code? Retry after{" "}
          <span className="font-hanken text-blackish dark:text-golden">
            {allowResendAfter}s
          </span>
        </p>
      )}
    </div>
  )
}

export default VerifyOtp
